import type { RasterSourceSpecification } from "maplibre-gl";

/**
 * Basemaps and imagery (ADR-007). OpenFreeMap vector styles need no key. Satellite imagery is a
 * raster source layered under the sites: USGS Imagery Only (NAIP, public domain, US only) by
 * default; Esri World Imagery when `VITE_ARCGIS_API_KEY` is set (ArcGIS Location Platform terms,
 * attribution required). Google properties are deep links only, never embedded.
 */

export const POSITRON_STYLE = "https://tiles.openfreemap.org/styles/positron";
export const LIBERTY_STYLE = "https://tiles.openfreemap.org/styles/liberty";

export const USGS_IMAGERY_TILES =
  "https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}";

export const ESRI_WORLD_IMAGERY_TILES =
  "https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token={key}";

export type BasemapId = "positron" | "liberty";

export const US_CENTER: [number, number] = [-98.5795, 39.8283];
export const US_ZOOM = 4.15;

export function basemapStyle(id: BasemapId): string {
  return id === "liberty" ? LIBERTY_STYLE : POSITRON_STYLE;
}

export function satelliteSource(arcgisKey?: string): RasterSourceSpecification {
  if (arcgisKey) {
    return {
      type: "raster",
      tiles: [ESRI_WORLD_IMAGERY_TILES.replace("{key}", encodeURIComponent(arcgisKey))],
      tileSize: 256,
      maxzoom: 19,
      attribution: "Esri World Imagery (ArcGIS Location Platform)",
    };
  }
  return {
    type: "raster",
    tiles: [USGS_IMAGERY_TILES],
    tileSize: 256,
    maxzoom: 19,
    attribution: "USGS NAIP / The National Map (public domain)",
  };
}

export function googleMapsSatellite(lat: number, lon: number): string {
  return `https://www.google.com/maps/@${lat},${lon},18z/data=!3m1!1e3`;
}

export function googleEarth(lat: number, lon: number): string {
  return `https://earth.google.com/web/@${lat},${lon},500a,1200d,35y,0h,0t,0r`;
}

export function streetView(lat: number, lon: number): string {
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lon}`;
}

export function openStreetMap(lat: number, lon: number): string {
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`;
}

/** Client-side LinkedIn search link (PRD §11.2: the system fetches nothing from LinkedIn). */
export function linkedInSearch(name: string, company: string | null): string {
  const q = [name, company].filter(Boolean).join(" ");
  return `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(q)}`;
}
